'use client'

import { localPathForZip } from '@/lib/local/zip-path'
import { useState } from 'react'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client';
import { MapPin, ChevronDown, Loader2, Zap } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'

interface CityWithZipCount {
  city_slug: string
  city_name: string
  zip_count: number
  initial_zip: string | null
}

interface ZipData {
  zip: string
  res_rate_kwh: number
}

interface StateZipBrowserProps {
  stateCode: string
  stateName: string
  stateSlug: string
  topCities: CityWithZipCount[]
}

export function StateZipBrowser({ stateCode, stateName, stateSlug, topCities }: StateZipBrowserProps) {
  const [expandedCity, setExpandedCity] = useState<string | null>(null)
  const [cityZips, setCityZips] = useState<Record<string, ZipData[]>>({})
  const [loading, setLoading] = useState<string | null>(null)

  const loadCityZips = async (citySlug: string) => {
    if (cityZips[citySlug]) {
      // Already loaded
      setExpandedCity(expandedCity === citySlug ? null : citySlug)
      return
    }

    setLoading(citySlug)
    setExpandedCity(citySlug)

    try {
      const supabase = createClient()

      const { data } = await supabase
        .from('zip_energy_data')
        .select('zip, res_rate_kwh')
        .eq('state_code', stateCode)
        .eq('city_slug', citySlug)
        .order('zip', { ascending: true })
        .limit(100)

      setCityZips(prev => ({
        ...prev,
        [citySlug]: data || []
      }))
    } catch {
      console.error('Failed to load ZIPs for', citySlug)
    } finally {
      setLoading(null)
    }
  }

  const formatCityName = (slug: string) => {
    return slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  if (topCities.length === 0) return null

  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Explore ZIP electricity rates in {stateName}</h2>
      <p className="text-sm text-gray-600 mb-3">Start with a ZIP from one of these cities, or browse more ZIP codes below.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {topCities.slice(0, 12).map(city => {
          const href = localPathForZip({ zip: city.initial_zip ?? '', state_code: stateCode, city_slug: city.city_slug })
          return href ? <Link key={city.city_slug} href={href}
            className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0469f0] hover:border-[#0469f0]">
            {city.city_name || formatCityName(city.city_slug)} · {city.initial_zip}
          </Link> : null
        })}
      </div>
      <Collapsible>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#0469f0]/5 to-[#0469f0]/10 border border-[#0469f0]/20 rounded-lg hover:bg-[#0469f0]/10 transition-colors">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-[#0469f0]" />
              <span className="text-lg font-semibold text-gray-900">
                Browse ZIP Codes by City
              </span>
            </div>
            <ChevronDown className="h-5 w-5 text-gray-500 transition-transform [[data-state=open]_&]:rotate-180" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-4 border-[#0469f0]/20">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Choose a city to browse up to 100 ZIP codes with electricity rates and refrigerator energy estimates.
              </p>
              <div className="space-y-2">
                {topCities.map(city => (
                  <div key={city.city_slug} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => loadCityZips(city.city_slug)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#0469f0]" />
                        <span className="font-medium text-gray-900">
                          {city.city_name || formatCityName(city.city_slug)}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({city.zip_count} ZIP{city.zip_count !== 1 ? 's' : ''})
                        </span>
                      </div>
                      {loading === city.city_slug ? (
                        <Loader2 className="h-4 w-4 text-[#0469f0] animate-spin" />
                      ) : (
                        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${expandedCity === city.city_slug ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {expandedCity === city.city_slug && cityZips[city.city_slug] && (
                      <div className="p-3 bg-white border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {cityZips[city.city_slug].map(zip => (
                            <Link
                              key={zip.zip}
                              href={`/local/${stateSlug}/${city.city_slug}/${zip.zip}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm hover:border-[#0469f0] hover:text-[#0469f0] transition-colors group"
                            >
                              <span className="font-medium">{zip.zip}</span>
                              <span className="text-xs text-gray-500 group-hover:text-[#0469f0]">
                                {(zip.res_rate_kwh * 100).toFixed(1)}¢
                              </span>
                            </Link>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <Link
                            href={`/local/${stateSlug}/${city.city_slug}`}
                            className="text-sm text-[#0469f0] hover:underline"
                          >
                            View full {city.city_name || formatCityName(city.city_slug)} page →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </section>
  )
}
